import { useState } from "react";
import { toast } from "sonner";
import { Plus, BadgeCheck, BadgeAlert, Trash2, Bot } from "lucide-react";
import { useUserBindings } from "@/api/queries/channelBindings";
import { useChannels } from "@/api/queries/channels";
import { useAgents } from "@/api/queries/agents";
import {
  useVerifyBinding,
  useDeleteBinding,
} from "@/api/mutations/channelBindings";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DateTimeCell } from "@/components/ResourceShell";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CreateBindingDialog } from "./CreateBindingDialog";
import { EditBindingAgentDialog } from "./EditBindingAgentDialog";

interface Props {
  userId: string;
}

export function BindingsSection({ userId }: Props) {
  const bindingsQuery = useUserBindings(userId);
  const channelsQuery = useChannels();
  const agentsQuery = useAgents();
  const verifyMutation = useVerifyBinding(userId);
  const deleteMutation = useDeleteBinding(userId);

  const [createOpen, setCreateOpen] = useState(false);
  const [editAgentTarget, setEditAgentTarget] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const bindings = bindingsQuery.data ?? [];
  const agentById = (id: string) =>
    (agentsQuery.data ?? []).find((a) => a.id === id);
  const channelById = (id: string) =>
    (channelsQuery.data ?? []).find((c) => c.id === id);

  function handleVerify(id: string) {
    verifyMutation.mutate(id, {
      onSuccess: () => toast.success("Binding verifyt"),
      onError: (err) => toast.error(`Verify fehlgeschlagen: ${(err as Error).message}`),
    });
  }

  function handleDelete() {
    if (!deleteTargetId) return;
    setDeleteError(null);
    deleteMutation.mutate(deleteTargetId, {
      onSuccess: () => {
        toast.success("Binding gelöscht");
        setDeleteTargetId(null);
      },
      onError: (err) => setDeleteError((err as Error).message),
    });
  }

  return (
    <>
      <Card className="mt-4" data-testid="user-bindings-section">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Channel-Bindings ({bindings.length})</CardTitle>
            <CardDescription>
              User × Channel × externe Identität → optional spezifischer Agent. Verified-Bindings dürfen System-Notifications empfangen.
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="binding-action-create">
            <Plus className="h-4 w-4" />
            Neue Binding
          </Button>
        </CardHeader>
        <CardContent>
          {bindingsQuery.isLoading ? (
            <p className="text-sm text-overlay0">Lädt…</p>
          ) : bindingsQuery.isError ? (
            <p className="text-sm text-red" data-testid="bindings-error">
              Bindings konnten nicht geladen werden. Seite neu laden.
            </p>
          ) : bindings.length === 0 ? (
            <p className="text-sm text-overlay0">
              Noch keine Bindings. Klick „Neue Binding" um eine externe Identität zu verknüpfen.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Channel</TableHead>
                  <TableHead>Externe Identität</TableHead>
                  <TableHead>Agent-Target</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Erstellt</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bindings.map((b) => {
                  const channel = channelById(b.channelId);
                  const verified = b.verifiedAt !== null;
                  const targetAgent = b.agentId ? agentById(b.agentId) : null;
                  return (
                    <TableRow key={b.id} data-testid={`binding-row-${b.id}`}>
                      <TableCell>
                        {channel ? (
                          <span>
                            {channel.name}{" "}
                            <Badge variant="outline">{channel.type}</Badge>
                          </span>
                        ) : (
                          <Badge variant="orphaned">orphaned: {b.channelId}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        <div>{b.externalId}</div>
                        {b.displayLabel && b.displayLabel !== b.externalId && (
                          <div className="text-overlay0">{b.displayLabel}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        {b.agentId === null ? (
                          <span className="text-overlay0 text-xs">— Fallback</span>
                        ) : targetAgent ? (
                          <Badge variant="outline" className="font-mono text-xs">
                            <Bot className="h-3 w-3 mr-1 inline" />
                            {targetAgent.id}
                          </Badge>
                        ) : (
                          <Badge variant="orphaned">orphaned: {b.agentId}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {verified ? (
                          <Badge variant="active" data-testid={`binding-status-${b.id}`}>
                            <BadgeCheck className="h-3 w-3 mr-1 inline" />
                            verified
                          </Badge>
                        ) : (
                          <Badge variant="disabled" data-testid={`binding-status-${b.id}`}>
                            <BadgeAlert className="h-3 w-3 mr-1 inline" />
                            unverified
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <DateTimeCell iso={b.createdAt} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditAgentTarget(b.id)}
                            data-testid={`binding-edit-agent-${b.id}`}
                          >
                            <Bot className="h-4 w-4" />
                          </Button>
                          {!verified && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleVerify(b.id)}
                              disabled={verifyMutation.isPending}
                              data-testid={`binding-verify-${b.id}`}
                            >
                              Verify
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setDeleteTargetId(b.id)}
                            data-testid={`binding-delete-${b.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {createOpen && (
        <CreateBindingDialog open onOpenChange={setCreateOpen} userId={userId} />
      )}
      {editAgentTarget && (
        <EditBindingAgentDialog
          open
          onOpenChange={(o) => !o && setEditAgentTarget(null)}
          userId={userId}
          binding={bindings.find((b) => b.id === editAgentTarget)!}
        />
      )}
      <ConfirmDialog
        open={deleteTargetId !== null}
        onOpenChange={(o) => !o && setDeleteTargetId(null)}
        title="Binding löschen"
        description="Diese Aktion ist NICHT umkehrbar. Falls dieses Binding als Default-Notification gesetzt war, wird die Referenz auf null gesetzt."
        confirmText="Löschen"
        confirmVariant="destructive"
        isPending={deleteMutation.isPending}
        errorMessage={deleteError ?? undefined}
        onConfirm={handleDelete}
      />
    </>
  );
}
